import { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@racefront/shadcn-ui'
import { Loader2 } from 'lucide-react'
import dayjs from 'dayjs'
import { DateInput } from './date-input'
import LocaleSelect from './locale-select'
import { useLocalization } from '@racefront/common/context/localization-context'

type SectionType = 'email' | 'phone' | 'locale' | 'personal' | 'address'

interface MobileSectionEditDialogProps {
  isOpen: boolean
  onClose: () => void
  section: SectionType
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>
  onSave: () => Promise<void>
  isLoading: boolean
}

export function MobileSectionEditDialog({
  isOpen,
  onClose,
  section,
  form,
  onSave,
  isLoading,
}: MobileSectionEditDialogProps) {
  const intl = useIntl()
  const { countries } = useLocalization()
  const [isSaving, setIsSaving] = useState(false)

  const handleApply = async () => {
    setIsSaving(true)
    try {
      await onSave()
      onClose()
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsSaving(false)
    }
  }

  const renderSectionContent = () => {
    switch (section) {
      case 'email':
        return (
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <FormattedMessage id="form.email_label" defaultMessage="Email" />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder={intl.formatMessage({
                      id: 'form.email_placeholder',
                      defaultMessage: 'your@email.com',
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case 'phone':
        return (
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <FormattedMessage id="form.phoneNumber_label" defaultMessage="Phone Number" />
                </FormLabel>
                <FormControl>
                  <PhoneInput
                    {...field}
                    placeholder={intl.formatMessage({
                      id: 'form.phoneNumber_placeholder',
                      defaultMessage: '+1234567890',
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case 'locale':
        return (
          <FormField
            control={form.control}
            name="locale"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <FormattedMessage id="form.locale_label" defaultMessage="Language" />
                </FormLabel>
                <LocaleSelect field={field} />
                <FormMessage />
              </FormItem>
            )}
          />
        )

      case 'personal':
        return (
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="firstname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage id="form.firstname_label" defaultMessage="First Name" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={intl.formatMessage({ id: 'form.firstname_placeholder', defaultMessage: 'John' })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage id="form.lastname_label" defaultMessage="Last Name" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={intl.formatMessage({ id: 'form.lastname_placeholder', defaultMessage: 'Doe' })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage id="form.gender_label" defaultMessage="Gender" />
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={intl.formatMessage({
                            id: 'form.gender_placeholder',
                            defaultMessage: 'Select gender',
                          })}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="0">
                        <FormattedMessage id="form.gender.not_known" defaultMessage="Not Known" />
                      </SelectItem>
                      <SelectItem value="1">
                        <FormattedMessage id="form.gender.male" defaultMessage="Male" />
                      </SelectItem>
                      <SelectItem value="2">
                        <FormattedMessage id="form.gender.female" defaultMessage="Female" />
                      </SelectItem>
                      <SelectItem value="3">
                        <FormattedMessage id="form.gender.not_applicable" defaultMessage="Not Applicable" />
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="birthdate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage id="form.birthdate_label" defaultMessage="Birth Date" />
                  </FormLabel>
                  <DateInput
                    field={field}
                    isRequired
                    formater={(date) => dayjs(date)?.format('YYYY-MM-DD')}
                    placeholder={<FormattedMessage id="form.birthdate_placeholder" defaultMessage="Pick a date" />}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nationality"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage id="form.nationality_label" defaultMessage="Nationality" />
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={intl.formatMessage({
                            id: 'form.nationality_placeholder',
                            defaultMessage: 'Select nationality',
                          })}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {countries?.map(({ code, name }) => (
                        <SelectItem key={code} value={code}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )

      case 'address':
        return (
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="street"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage id="form.street_label" defaultMessage="Street" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={intl.formatMessage({
                        id: 'form.street_placeholder',
                        defaultMessage: '123 Main St',
                      })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage id="form.city_label" defaultMessage="City" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={intl.formatMessage({ id: 'form.city_placeholder', defaultMessage: 'New York' })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="postCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage id="form.postCode_label" defaultMessage="Post Code" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={intl.formatMessage({ id: 'form.postCode_placeholder', defaultMessage: '10001' })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage id="form.country_label" defaultMessage="Country" />
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={intl.formatMessage({
                            id: 'form.country_placeholder',
                            defaultMessage: 'Select country',
                          })}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {countries?.map(({ code, name }) => (
                        <SelectItem key={code} value={code}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )

      default:
        return null
    }
  }

  const getSectionTitle = () => {
    switch (section) {
      case 'email':
        return intl.formatMessage({ id: 'form.section.email', defaultMessage: 'Email Address' })
      case 'phone':
        return intl.formatMessage({ id: 'form.section.phone', defaultMessage: 'Phone Number' })
      case 'locale':
        return intl.formatMessage({ id: 'form.section.language', defaultMessage: 'Language Preference' })
      case 'personal':
        return intl.formatMessage({ id: 'form.section.personal_info', defaultMessage: 'Personal Information' })
      case 'address':
        return intl.formatMessage({ id: 'form.section.address_info', defaultMessage: 'Address Information' })
      default:
        return ''
    }
  }

  const getSectionDescription = () => {
    switch (section) {
      case 'email':
        return intl.formatMessage({
          id: 'form.section.email.description',
          defaultMessage: 'Your email address will be used for account recovery and important notifications.',
        })
      case 'phone':
        return intl.formatMessage({
          id: 'form.section.phone.description',
          defaultMessage: 'Your phone number for account verification and contact purposes.',
        })
      case 'locale':
        return intl.formatMessage({
          id: 'form.section.language.description',
          defaultMessage: 'Choose your preferred language for the application interface.',
        })
      case 'personal':
        return intl.formatMessage({
          id: 'form.section.personal_info.description',
          defaultMessage: 'Your personal details including name, birth date, gender, and nationality.',
        })
      case 'address':
        return intl.formatMessage({
          id: 'form.section.address_info.description',
          defaultMessage: 'Your residential address for billing and delivery purposes.',
        })
      default:
        return ''
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-full w-full max-w-full overflow-y-auto sm:h-auto sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{getSectionTitle()}</DialogTitle>
          <DialogDescription>{getSectionDescription()}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="py-4">{renderSectionContent()}</div>
        </Form>

        <DialogFooter className="flex-row items-end justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving || isLoading} className="flex-1">
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="button" onClick={handleApply} disabled={isSaving || isLoading} className="flex-1">
            {(isSaving || isLoading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <FormattedMessage id="common.save" defaultMessage="Save" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
